import type { Carer, Home } from '@lmc/ts-common'
import { PageHeader } from '@lmc/ui-kit'
import { Tabs } from 'antd'
import { canManagePayAndInvoicing } from 'common/permissions'
import { getNominative } from 'common/utils'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Navigate, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import type { ChargeReportToReconciliationNavigationState } from '../../Reports/types/chargeReport'
import { ChargeRatesReconciliationTab } from './ChargeRates/ChargeRatesReconciliationTab'
import { PayRatesReconciliationTab } from './PayRatesReconciliationTab'

type ReconciliationTabKey = 'pay-rates' | 'charge-rates'

export const ReconciliationPage = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const userRole = useSelector(
    (state: { global: { user: Carer } }) => state.global.user?.role
  )
  const canAccess = canManagePayAndInvoicing(userRole)

  // Master gate for the entire Charge Rates work. When this flag is off the
  // page renders exactly as it did before charge rates existed: no tabs, the
  // original subtitle, and none of the ChargeRates code is mounted.
  const invoicingReconciliationReportEnabled = useFeatureFlags({
    flagNames: ['invoicing_reconciliation_report']
  })?.data?.invoicing_reconciliation_report?.enabled

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )
  const nominative = getNominative(preferredPeopleNominative, 'Resident')

  // Deep-link from the Charge Report status tag lands on the Charge Rates tab.
  const cameFromChargeReport =
    (location.state as ChargeReportToReconciliationNavigationState | null)
      ?.fromChargeReport === true
  const [activeTab, setActiveTab] = useState<ReconciliationTabKey>(
    cameFromChargeReport ? 'charge-rates' : 'pay-rates'
  )

  useEffect(() => {
    if (cameFromChargeReport) {
      setActiveTab('charge-rates')
    }
  }, [cameFromChargeReport])

  if (!canAccess) {
    return <Navigate to="/rota/builder" replace />
  }

  if (!invoicingReconciliationReportEnabled) {
    return (
      <>
        <PageHeaderSection>
          <PageHeader
            title={t('RECONCILIATION')}
            subtitle={t('RECONCILIATION_INTRO')}
          />
        </PageHeaderSection>

        <PageBody>
          <PayRatesReconciliationTab nominative={nominative} />
        </PageBody>
      </>
    )
  }

  const items = [
    {
      key: 'pay-rates',
      label: t('RECONCILIATION_TAB_PAY_RATES'),
      children: <PayRatesReconciliationTab nominative={nominative} />
    },
    {
      key: 'charge-rates',
      label: t('RECONCILIATION_TAB_CHARGE_RATES'),
      children: <ChargeRatesReconciliationTab nominative={nominative} />
    }
  ]

  return (
    <>
      <PageHeaderSection>
        <PageHeader
          title={t('RECONCILIATION')}
          subtitle={t('RECONCILIATION_INTRO_CHARGE_RATES')}
        />
      </PageHeaderSection>

      <PageBody>
        <StyledTabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as ReconciliationTabKey)}
          items={items}
        />
      </PageBody>
    </>
  )
}

export default ReconciliationPage

const PageHeaderSection = styled.div`
  padding: 24px 48px 0;
`

const PageBody = styled.div`
  padding: 24px 48px 48px;
`

const StyledTabs = styled(Tabs)`
  & > .ant-tabs-nav {
    margin-bottom: 24px;
  }
`
